import {
  Error as ErrorIcon,
  LiveHelp as LiveHelpIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * "Contact Support" button for the error boundary. Opens a mailto with
 * the error pre-filled. Works at any provider depth.
 */
function ContactSupportButton({ error }: { error: Error }) {
  const subject = encodeURIComponent("App Error Report");
  const body = encodeURIComponent(
    `Hi, I encountered an error in the app:\n\n${error.toString()}\n\nPlease help!`,
  );

  return (
    <Button
      variant="outlined"
      startIcon={<LiveHelpIcon />}
      href={`mailto:contact@desplega.ai?subject=${subject}&body=${body}`}
      size="small"
    >
      Contact Support
    </Button>
  );
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error);
    console.error("Error details:", errorInfo);
    console.error("Component stack:", errorInfo.componentStack);

    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack ?? undefined },
      },
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Stack
          data-testid="error-boundary"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            px: 3,
            py: 4,
            backgroundColor: "background.default",
          }}
          spacing={3}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "error.main",
              color: "white",
              mb: 2,
            }}
          >
            <ErrorIcon sx={{ fontSize: 48 }} />
          </Box>

          <Typography variant="h4" gutterBottom textAlign="center">
            Oops! Something went wrong
          </Typography>

          <Typography variant="h6" color="text.secondary" textAlign="center">
            We encountered an unexpected error. This has been logged for our team.
          </Typography>

          <Divider sx={{ width: "100%", maxWidth: 400, my: 2 }} />

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={this.handleReload}
            size="large"
          >
            Reload Page
          </Button>

          {this.state.error && (
            <Paper
              elevation={0}
              sx={{
                mt: 4,
                p: 3,
                maxWidth: 800,
                width: "100%",
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle2" color="error" gutterBottom>
                Error Details:
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 2,
                  backgroundColor: "grey.100",
                  borderRadius: 1,
                  overflow: "auto",
                  fontSize: "0.875rem",
                  fontFamily: "monospace",
                  maxHeight: 200,
                }}
              >
                {this.state.error.toString()}
              </Box>

              {this.state.errorInfo && (
                <>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ mt: 2 }}
                  >
                    Component Stack:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      backgroundColor: "grey.100",
                      borderRadius: 1,
                      overflow: "auto",
                      fontSize: "0.75rem",
                      fontFamily: "monospace",
                      maxHeight: 300,
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </Box>
                </>
              )}
            </Paper>
          )}

          <Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              If this problem persists:
            </Typography>
            <ContactSupportButton error={this.state.error!} />
          </Stack>
        </Stack>
      );
    }

    return this.props.children;
  }
}
